import { useState, useEffect } from "react";
import { 
  FaPrint, FaEdit, FaUser, FaPhoneAlt, FaProcedures, 
  FaCalendarAlt, FaUserMd, FaMapMarkerAlt 
} from "react-icons/fa";
import { RiDeleteBin6Line } from "react-icons/ri";
import { HiXMark } from "react-icons/hi2";
import { format } from "date-fns";
import { NicuPrintLayout } from "../NicuPrintLayout";

const NicuViewAdmissionModal = ({ 
  isOpen, 
  onClose, 
  admission: initialAdmission, 
  onEdit, 
  onDelete 
}) => {
  const [admission, setAdmission] = useState(initialAdmission);

  useEffect(() => {
    setAdmission(initialAdmission);
  }, [initialAdmission]);

  if (!isOpen || !admission) return null;

  const {
    regNo,
    specialNo,
    patientName,
    fatherName,
    motherName,
    relativeName,
    careOf,
    relationship,
    age,
    sex,
    religion,
    address,
    dateOfBirth,
    phoneNumber,
    relativeMobileNumber,
    admissionDate,
    admissionTime,
    consultantName,
    agentName,
    bedNo,
    wardCabin,
    billingType,
    status,
    employeeName,
    primaryDiagnosis,
    employeeCode
  } = admission;

  const handlePrint = () => {
    const elementsToHide = document.querySelectorAll(".print-hidden");
    elementsToHide.forEach((el) => (el.style.display = "none"));

    const printLayout = document.querySelector(".print-layout");
    if (printLayout) {
      printLayout.style.display = "block";
    }
    window.print();
    setTimeout(() => {
      elementsToHide.forEach((el) => (el.style.display = ""));
      if (printLayout) {
        printLayout.style.display = "none";
      }
    }, 500);
  };

  const handleEditClick = () => {
    if (onEdit) {
      onEdit(admission);
      onClose();
    }
  };

  const handleDeleteClick = () => {
    if (onDelete) {
      onDelete(admission.id);
      onClose();
    }
  };

const admissionPrintInfo = (
    <div className="w-full font-sans text-gray-800">
      {/* --- TOP: NAME + REG NO --- */}
      <div className="flex justify-between items-end border-b-2 border-gray-800 pb-3 mb-5">
        <div className="pr-4">
          <p className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-1">Patient Name</p>
          <h2 className="text-2xl font-extrabold text-gray-900 uppercase leading-none whitespace-nowrap">
            {patientName || "Unknown Patient"}
          </h2>
        </div>
        <div className="text-right shrink-0">
          <p className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-1">Registration No</p>
          <h2 className="text-2xl font-extrabold text-gray-900 leading-none whitespace-nowrap">
            {regNo || "N/A"}
          </h2>
        </div>
      </div>

      {/* --- SECTION 1: PATIENT & GUARDIAN --- */}
      <div className="mb-6">
        <h3 className="text-sm font-bold uppercase tracking-wider border-b border-gray-400 pb-1 mb-3">
          Patient &amp; Guardian Information
        </h3>
        <div className="grid grid-cols-2 gap-x-12 gap-y-2 text-base">
          <div className="flex items-baseline">
            <span className="w-28 shrink-0 text-gray-500 font-semibold">Age / Sex:</span>
            <span className="font-bold whitespace-nowrap">{age || "--"} / {sex || "--"}</span>
          </div>
          <div className="flex items-baseline">
            <span className="w-32 shrink-0 text-gray-500 font-semibold whitespace-nowrap">Father:</span>
            <span className="font-bold whitespace-nowrap">{fatherName || "--"}</span>
          </div>

          <div className="flex items-baseline">
            <span className="w-28 shrink-0 text-gray-500 font-semibold">Date of Birth:</span>
            <span className="font-bold whitespace-nowrap">
              {dateOfBirth ? format(new Date(dateOfBirth), "dd MMM yyyy") : "--"}
            </span>
          </div>
          <div className="flex items-baseline">
            <span className="w-32 shrink-0 text-gray-500 font-semibold">Mother:</span>
            <span className="font-bold whitespace-nowrap">{motherName || "--"}</span>
          </div>

          <div className="flex items-baseline">
            <span className="w-28 shrink-0 text-gray-500 font-semibold">Religion:</span>
            <span className="font-bold whitespace-nowrap">{religion || "--"}</span>
          </div>
          <div className="flex items-baseline">
                <span className="w-32 shrink-0 text-gray-500 font-semibold">Guardian:</span>
                <span className="font-bold whitespace-nowrap">{relativeName || "--"}</span>
              </div>

          <div className="flex items-baseline">
            <span className="w-28 shrink-0 text-gray-500 font-semibold">Mobile:</span>
            <span className="font-bold whitespace-nowrap">{phoneNumber || "--"}</span>
          </div>
          <div className="flex items-baseline">
            <span className="w-32 shrink-0 text-gray-500 font-semibold whitespace-nowrap">Contact Person:</span>
            <span className="font-bold whitespace-nowrap">
              {relativeName || "--"}{relationship ? ` (${relationship})` : ""}
            </span>
          </div>

          <div className="col-span-2 flex items-baseline pt-1">
            <span className="w-28 shrink-0 text-gray-500 font-semibold">Full Address:</span>
            <span className="font-bold">
              {address || "N/A"}{careOf ? ` (C/O: ${careOf})` : ""}
            </span>
          </div>
        </div>
      </div>

      {/* --- SECTION 2: ADMISSION DETAILS --- */}
      <div>
        <h3 className="text-sm font-bold uppercase tracking-wider border-b border-gray-400 pb-1 mb-3">
          Admission Details
        </h3>
        <div className="grid grid-cols-2 gap-x-12 gap-y-2 text-base">
          <div className="flex items-baseline">
            <span className="w-28 shrink-0 text-gray-500 font-semibold">Admit Date:</span>
            <span className="font-bold whitespace-nowrap">
              {admissionDate ? format(new Date(admissionDate), "dd MMM yyyy") : "--"}
            </span>
          </div>
          <div className="flex items-baseline">
            <span className="w-32 shrink-0 text-gray-500 font-semibold">Status:</span>
            <span className="font-bold whitespace-nowrap">{status || "--"}</span>
          </div>

          <div className="flex items-baseline">
            <span className="w-28 shrink-0 text-gray-500 font-semibold">Admit Time:</span>
            <span className="font-bold whitespace-nowrap">{admissionTime || "--"}</span>
          </div>
          <div className="flex items-baseline">
            <span className="w-32 shrink-0 text-gray-500 font-semibold">Bill Type:</span>
            <span className="font-bold whitespace-nowrap">{billingType || "Normal"}</span>
          </div>

          <div className="flex items-baseline">
            <span className="w-28 shrink-0 text-gray-500 font-semibold">Bed No:</span>
            <span className="font-bold whitespace-nowrap border border-gray-400 px-2">{bedNo || "None"}</span>
          </div>

          <div className="col-span-2 flex items-baseline pt-1">
            <span className="w-28 shrink-0 text-gray-500 font-semibold">Consultant:</span>
            <span className="font-bold whitespace-nowrap">{consultantName || "N/A"}</span>
          </div>
        </div>
      </div>

      {/* --- METADATA --- */}
      <div className="mt-6 pt-2 border-t border-dashed border-gray-300 flex justify-between text-[10px] text-gray-500">
            <span>Entry By: {employeeName} ({employeeCode})</span>
            <span>Printed: {new Date().toLocaleString("en-GB")}</span>
          </div>
    </div>
  );

  // --- 2. Define Helper for MODAL View (Grid Format) ---
  const DetailBlock = ({ label, value, icon }) => (
    <div className="flex flex-col mb-1">
      <span className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold mb-0.5 flex items-center gap-1">
        {icon && <span className="text-gray-400">{icon}</span>}
        {label}
      </span>
      <span className="text-sm font-medium text-gray-800 break-words">{value || "—"}</span>
    </div>
  );

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-4 print-hidden transition-opacity duration-300">
        <div className="bg-white shadow-2xl w-full max-w-5xl rounded-xl overflow-hidden flex flex-col max-h-[90vh] animate-fadeIn">
          
          {/* --- Modal Header --- */}
          <div className="flex items-center justify-between gap-3 flex-wrap bg-gray-200 border-b border-dashed border-gray-300 p-4 print-hidden">
            <div>
              <h2 className="text-xl font-bold text-gray-800">NICU Admission Details</h2>
              <p className="text-xs text-gray-500 mt-0.5">View full patient admission information</p>
            </div>
            
            <div className="flex items-center gap-3">
              <button onClick={handlePrint} className="size-7 rounded-full bg-white text-M-primary-color text-sm inline-flex items-center justify-center hover:bg-M-primary-color hover:text-white transition-all duration-300" title="Print">
                <FaPrint />
              </button>
              <button onClick={handleEditClick} className="size-7 rounded-full bg-white text-green-600 text-sm inline-flex items-center justify-center hover:bg-green-600 hover:text-white transition-all duration-300" title="Edit">
                <FaEdit />
              </button>
              <button onClick={handleDeleteClick} className="size-7 rounded-full bg-white text-red-600 text-sm inline-flex items-center justify-center hover:bg-red-600 hover:text-white transition-all duration-300" title="Delete">
                <RiDeleteBin6Line />
              </button>
              <button onClick={onClose} className="size-7 rounded-full bg-white text-gray-600 text-sm inline-flex items-center justify-center hover:bg-gray-600 hover:text-white transition-all duration-300" title="Close">
                <HiXMark />
              </button>
            </div>
          </div>

          {/* --- Modal Body --- */}
          <div className="p-6 overflow-y-auto bg-gray-50/50">
            
            {/* Patient Hero Card */}
            <div className="bg-white p-5 rounded-lg shadow-sm border border-gray-100 mb-6 flex flex-col md:flex-row justify-between md:items-center gap-4">
              <div className="flex items-start gap-4">
                <div className="size-14 rounded-full bg-blue-50 flex items-center justify-center text-blue-600 text-2xl">
                  <FaUser />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-800">{patientName || "Unknown Patient"}</h3>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-600 mt-1">
                    <span className="flex items-center gap-1"><span className="font-semibold">Reg:</span> {regNo}</span>
                    <span>{age}</span>
                    <span>{sex}</span>
                    {specialNo && <span className="text-blue-600">Special: {specialNo}</span>}
                  </div>
                </div>
              </div>
              <div className="flex gap-3">
                <div className="px-4 py-2 bg-blue-50 rounded-lg text-center min-w-[100px]">
                  <p className="text-xs text-blue-500 font-bold uppercase">Bed No</p>
                  <p className="text-lg font-bold text-blue-700">{bedNo || "N/A"}</p>
                </div>
                <div className={`px-4 py-2 rounded-lg text-center min-w-[100px] ${status === 'Released' ? 'bg-green-50' : 'bg-orange-50'}`}>
                  <p className={`text-xs font-bold uppercase ${status === 'Released' ? 'text-green-500' : 'text-orange-500'}`}>Status</p>
                  <p className={`text-lg font-bold ${status === 'Released' ? 'text-green-700' : 'text-orange-700'}`}>{status || "Admitted"}</p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Admission Info */}
              <div className="bg-white p-5 rounded-lg shadow-sm border border-gray-100">
                <h4 className="text-sm font-bold text-gray-800 uppercase border-b pb-2 mb-4 flex items-center gap-2">
                  <FaProcedures className="text-blue-500" /> Admission Details
                </h4>
                <div className="space-y-4">
                  <DetailBlock label="Admission Date" value={admissionDate ? format(new Date(admissionDate), "dd MMM yyyy") : ""} icon={<FaCalendarAlt size={10}/>} />
                  <DetailBlock label="Admission Time" value={admissionTime} />
                  <DetailBlock label="Consultant" value={consultantName} icon={<FaUserMd size={10}/>} />
                  <DetailBlock label="Ward / Cabin" value={wardCabin} />
                  <DetailBlock label="Billing Type" value={billingType} />
                  <DetailBlock label="Admitted By" value={employeeName} />
                </div>
              </div>

              {/* Personal Info */}
              <div className="bg-white p-5 rounded-lg shadow-sm border border-gray-100">
                <h4 className="text-sm font-bold text-gray-800 uppercase border-b pb-2 mb-4 flex items-center gap-2">
                  <FaUser className="text-green-500" /> Personal Info
                </h4>
                <div className="space-y-4">
                  <DetailBlock label="Father's Name" value={fatherName} />
                  <DetailBlock label="Mother's Name" value={motherName} />
                  <DetailBlock label="Date of Birth" value={dateOfBirth ? format(new Date(dateOfBirth), "dd MMM yyyy") : ""} />
                  <DetailBlock label="Religion" value={religion} />
                  <DetailBlock label="Phone Mobile" value={phoneNumber} icon={<FaPhoneAlt size={10}/>} />
                  <DetailBlock label="Address" value={address} icon={<FaMapMarkerAlt size={10}/>} />
                </div>
              </div>

              {/* Emergency Info */}
              <div className="bg-white p-5 rounded-lg shadow-sm border border-gray-100">
                <h4 className="text-sm font-bold text-gray-800 uppercase border-b pb-2 mb-4 flex items-center gap-2">
                  <FaPhoneAlt className="text-orange-500" /> Emergency / Other
                </h4>
                <div className="space-y-4">
                  <DetailBlock label="Relative Name" value={relativeName} />
                  <DetailBlock label="Relationship" value={relationship} />
                  <DetailBlock label="Relative Mobile" value={relativeMobileNumber} />
                  <DetailBlock label="Care Of (C/O)" value={careOf} />
                  <div className="pt-2 border-t border-dashed mt-2">
                    <DetailBlock label="Ref / Agent" value={agentName} />
                    <DetailBlock label="Diagnosis" value={primaryDiagnosis} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Hidden Print Layout — same design as the Admission page */}
      <NicuPrintLayout
        className="print-layout"
        title="NICU Admission Form"
        billInfo={admissionPrintInfo}
      />
    </>
  );
};

export default NicuViewAdmissionModal;